/**
 * A new approval workflow. Two ways in, said plainly: name your own and add
 * its steps on the next page, or start from an example that comes with its
 * steps. Either way, what it is FOR is asked here, because a workflow that
 * covers nothing is followed by no document.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useApprovalWorkflows, useCreateWorkflow, useCreateWorkflowWithSteps, useSetCoverage } from './approvalsData';
import { useTaxonomy } from '../admin/useTaxonomy';
import { MultiSelect } from '../../shared/MultiSelect';
import { BackLink } from '../../shared/BackLink';
import { WorkflowExamples } from './WorkflowExamples';
import { WorkflowNameFields, type WorkflowText } from './WorkflowNameFields';
import type { WorkflowExample } from './exampleChains';
import { isCallFailure } from '../../lib/callable';
import styles from '../../shared/Page.module.css';
import own from './Approvals.module.css';

const LIST = '/admin/approval-workflows';
const errorText = (e: unknown) => (isCallFailure(e) || e instanceof Error ? e.message : 'That could not be created.');

export function NewApprovalWorkflowPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const workflows = useApprovalWorkflows(Boolean(session));
  const typeItems = useTaxonomy('documentType', Boolean(session));
  const departmentItems = useTaxonomy('department', Boolean(session));
  const types = (typeItems.data ?? []).filter((t) => t.isActive);
  const departments = (departmentItems.data ?? []).filter((d) => d.isActive);
  /**
   * WHETHER THIS SHOULD BE THE CATCH-ALL IS NOT KNOWN UNTIL THE LIST IS.
   * `hasEverythingElse` used to be false while the list was still loading, so
   * a quick click sent `isDefault: true` and the server refused the whole
   * creation — "it cannot take over from Policy approval" — naming a decision
   * the person never made. Now the question has three answers, and only a
   * known one is acted on.
   */
  const known = !workflows.isLoading && !workflows.error;
  const hasEverythingElse = known && (workflows.data?.workflows ?? []).some((w) => w.isActive !== false && w.isDefault);

  const create = useCreateWorkflow();
  const setCoverage = useSetCoverage();
  const fromExample = useCreateWorkflowWithSteps();

  const [words, setWords] = useState<WorkflowText>({ name: '', description: '', longDescription: '' });
  const [typeIds, setTypeIds] = useState<string[]>([]);
  const [departmentIds, setDepartmentIds] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);
  const [coverageProblem, setCoverageProblem] = useState<{ workflowId: string; message: string } | null>(null);

  // With no workflow for everything else yet, the first one made should be it,
  // unless it names something. Never claimed while the list is unknown: an
  // organization that already has a catch-all must not have it displaced by a
  // guess, so an unknown list means "no claim", which the server accepts.
  const asEverythingElse = known && !hasEverythingElse && typeIds.length === 0 && departmentIds.length === 0;
  const busy = create.isPending || setCoverage.isPending || fromExample.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!words.name.trim()) return;
    create.mutate(
      { name: words.name.trim(), description: words.description.trim() || undefined, longDescription: words.longDescription.trim() || undefined, artifactType: 'policy', isDefault: asEverythingElse },
      {
        onSuccess: (made) => {
          const names = !asEverythingElse && (typeIds.length > 0 || departmentIds.length > 0);
          if (!names) return navigate(`${LIST}/${made.workflowId}`);
          setCoverage.mutate(
            { workflowId: made.workflowId, documentTypeIds: typeIds, departmentIds },
            {
              onSuccess: () => navigate(`${LIST}/${made.workflowId}`),
              // The workflow exists; only what it covers was refused. Say so, and offer the way on.
              onError: (err) => setCoverageProblem({ workflowId: made.workflowId, message: errorText(err) }),
            },
          );
        },
      },
    );
  }

  function createExample(ex: WorkflowExample) {
    const wanted = (ex.forTypes ?? []).map((n) => n.toLowerCase());
    const documentTypeIds = types.filter((t) => wanted.includes(t.name.toLowerCase())).map((t) => t.id);
    setCreatingKey(ex.key);
    fromExample.mutate(
      { name: ex.name, description: ex.description, exampleKey: ex.key, makeDefault: known && !hasEverythingElse && documentTypeIds.length === 0, steps: ex.steps, ...(documentTypeIds.length ? { documentTypeIds, departmentIds: [] } : {}) },
      {
        onSuccess: (res) => (res.coverageRefused ? setCoverageProblem({ workflowId: res.workflowId, message: res.coverageRefused }) : navigate(`${LIST}/${res.workflowId}`)),
        onSettled: () => setCreatingKey(null),
      },
    );
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          <div className={styles.titleLine}>
            <BackLink to={LIST} label="Back to approval workflows" />
            <h1 className={styles.title}>New approval workflow</h1>
          </div>
          <p className={styles.subtitle}>Name it and say which documents follow it. You add its steps on the next page.</p>
        </div>
      </header>

      <div className={styles.body}>
        {coverageProblem && (
          <div className={`${styles.notice} ${styles.noticeWarn}`} role="alert">
            <p>The workflow was created, but it covers nothing yet: {coverageProblem.message}</p>
            <button type="button" className={styles.btn} onClick={() => navigate(`${LIST}/${coverageProblem.workflowId}`)}>Open it and choose what it is for</button>
          </div>
        )}
        {(create.error || fromExample.error) && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">{errorText(create.error ?? fromExample.error)}</div>
        )}

        <form onSubmit={submit}>
          <section className={styles.section} aria-labelledby="own-title">
            <h2 className={styles.sectionTitle} id="own-title">Your own workflow</h2>
            <WorkflowNameFields idPrefix="new" value={words} onChange={setWords} disabled={busy} nameMissing={touched && !words.name.trim()} />

            <h3 className={own.label} style={{ fontSize: 'var(--fs-md)', marginTop: 'var(--sp-5)' }}>Which documents follow it</h3>
            <p className={own.itemMeta}>
              Choose document types, departments, or both. Both means a document needs one of the types and one of the
              departments. A document follows the most specific workflow that covers it.
            </p>
            <div className={own.coverGrid}>
              <div>
                <div className={own.label} aria-hidden="true">Document types</div>
                <MultiSelect id="new-types" label="Document types" options={types} value={typeIds} onChange={setTypeIds} placeholder="Any document type" disabled={busy} />
              </div>
              <div>
                <div className={own.label} aria-hidden="true">Departments</div>
                <MultiSelect id="new-departments" label="Departments" options={departments} value={departmentIds} onChange={setDepartmentIds} placeholder="Any department" disabled={busy} />
              </div>
            </div>
            {!known ? (
              <p className={own.itemMeta}>
                {workflows.error
                  ? 'Your other workflows could not be loaded, so this one is created covering only what you chose above. You can make it the workflow for everything else from its own page.'
                  : 'Checking your other workflows\u2026'}
              </p>
            ) : hasEverythingElse ? (
              <p className={own.itemMeta}>
                To make this the workflow for everything no other workflow covers, create it, add its steps, then choose
                &ldquo;Use for everything else&rdquo; on its page. A workflow with no steps cannot take that place.
              </p>
            ) : (
              <p className={own.itemMeta}>
                {typeIds.length === 0 && departmentIds.length === 0
                  ? 'No workflow takes everything else yet, so this one will.'
                  : 'No workflow takes everything else yet. This one covers only what you chose above.'}
              </p>
            )}
            {!asEverythingElse && typeIds.length === 0 && departmentIds.length === 0 && (
              <p className={own.itemMeta}>Nothing is chosen, so no document will follow it until you say what it is for.</p>
            )}
            <div className={styles.actions} style={{ marginTop: 'var(--sp-4)' }}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{create.isPending || setCoverage.isPending ? 'Creating…' : 'Create and add steps'}</button>
            </div>
          </section>
        </form>

        <section className={styles.section} aria-labelledby="examples-title">
          <h2 className={styles.sectionTitle} id="examples-title">Or start from an example</h2>
          <p className={own.itemMeta}>
            Each comes with the steps shown. The ones made for particular document types are created covering them.
            You can rename it, change its steps and change what it is for afterward.
          </p>
          <WorkflowExamples creating={creatingKey} onCreate={createExample} />
        </section>
      </div>
    </>
  );
}
